import { Model, StructureElement, StructureProperties, Unit } from '../../mol-model/structure';
import { MoleculeType } from '../../mol-model/structure/model/types';
import { Residue } from './types';

type ResidueEntityType = Residue['entityType'];

export function residueEntityType(value: string | undefined, moleculeType: MoleculeType): ResidueEntityType {
    if (value === 'polymer' || value === 'branched' || value === 'water') return value;
    if (value === 'non-polymer') return moleculeType === MoleculeType.Ion ? 'ion' : 'ligand';
    return 'unknown';
}

/** Canonical compact residue identity: ref/author-chain/author-sequence+insertion-code/component. */
export function residueId(ref: string, chainAuth: string, seqAuth: number, insCode: string | undefined, comp: string): string {
    return `${ref}/${chainAuth}/${seqAuth}${insCode ?? ''}/${comp}`;
}

/** Preserve both numbering systems while deriving a stable residue identity from a real atomic location. */
export function residueFromLocation(ref: string, location: StructureElement.Location): Residue | undefined {
    if (!Unit.isAtomic(location.unit)) return void 0;
    const chainAuth = StructureProperties.chain.auth_asym_id(location);
    const chainLabel = StructureProperties.chain.label_asym_id(location);
    const seqAuth = StructureProperties.residue.auth_seq_id(location);
    const rawSeqLabel = StructureProperties.residue.label_seq_id(location);
    const seqLabel = rawSeqLabel > 0 ? rawSeqLabel : void 0;
    const insCode = StructureProperties.residue.pdbx_PDB_ins_code(location) || void 0;
    const comp = StructureProperties.residue.label_comp_id(location);
    const residueIndex = location.unit.model.atomicHierarchy.residueAtomSegments.index[location.element];
    const moleculeType = location.unit.model.atomicHierarchy.derived.residue.moleculeType[residueIndex];
    return { ref, chainAuth, chainLabel, seqAuth, seqLabel, insCode, comp, entityType: residueEntityType(StructureProperties.entity.type(location), moleculeType), id: residueId(ref, chainAuth, seqAuth, insCode, comp) };
}

/** Preserve both numbering systems while deriving a stable residue identity from model tables. */
export function atomIdsFromLoci(ref: string, loci: StructureElement.Loci): readonly string[] {
    const ids = new Set<string>();
    StructureElement.Loci.forEachLocation(loci, location => {
        const residue = residueFromLocation(ref, location);
        if (residue) ids.add(`${residue.id}/${StructureProperties.atom.label_atom_id(location)}`);
    });
    return [...ids];
}

export function residueFromModel(ref: string, model: Model, residueIndex: number): Residue {
    const element = model.atomicHierarchy.residueAtomSegments.offsets[residueIndex];
    const chainIndex = model.atomicHierarchy.chainAtomSegments.index[element];
    const chainAuth = model.atomicHierarchy.chains.auth_asym_id.value(chainIndex);
    const chainLabel = model.atomicHierarchy.chains.label_asym_id.value(chainIndex);
    const seqAuth = model.atomicHierarchy.residues.auth_seq_id.value(residueIndex);
    const rawSeqLabel = model.atomicHierarchy.residues.label_seq_id.value(residueIndex);
    const seqLabel = rawSeqLabel > 0 ? rawSeqLabel : void 0;
    const insCode = model.atomicHierarchy.residues.pdbx_PDB_ins_code.value(residueIndex) || void 0;
    const comp = model.atomicHierarchy.atoms.label_comp_id.value(element);
    const entityId = model.atomicHierarchy.chains.label_entity_id.value(chainIndex);
    const entityIndex = model.entities.getEntityIndex(entityId);
    const type = entityIndex >= 0 ? model.entities.data.type.value(entityIndex) : void 0;
    const moleculeType = model.atomicHierarchy.derived.residue.moleculeType[residueIndex];
    return { ref, chainAuth, chainLabel, seqAuth, seqLabel, insCode, comp, entityType: residueEntityType(type, moleculeType), id: residueId(ref, chainAuth, seqAuth, insCode, comp) };
}
